import { usePermissions } from '@ltvco/refresh-lib/ctx';
import {
  Card,
  Stack,
  Text,
  Box,
  Divider,
  useTheme,
} from '@ltvco/refresh-lib/theme';
import { commaFormatCurrency } from '@ltvco/refresh-lib/utils';
import {
  AccessTime,
  BuildOutlined,
  ErrorOutlineOutlined,
  ForumOutlined,
  Info,
  PersonPinOutlined,
} from '@mui/icons-material';
import React from 'react';

import { getDifficultyColor, getUrgencyColor } from '../utils';

import type { RepairCostEstimate } from '@ltvco/refresh-lib/api';

export interface IRepairCostEstimatorResultCardProps {
  estimateData?: RepairCostEstimate;
}

/**
 * RepairCostEstimatorResultCard - Displays repair cost estimate with detailed breakdown
 * @component
 */
export const RepairCostEstimatorResultCard: React.FC<
  IRepairCostEstimatorResultCardProps
> = ({ estimateData }) => {
  const theme = useTheme();
  const { isFreePlan } = usePermissions();

  if (!estimateData) {
    return null;
  }

  const {
    repair_type,
    estimated_cost_range,
    average_cost,
    parts_cost_range,
    labor_cost_range,
    repair_time,
    difficulty,
    urgency,
    questions,
    pro_tip,
  } = estimateData;

  const cardDetails = [
    {
      icon: AccessTime,
      label: 'Repair Time',
      value: repair_time,
      color: theme.palette.success.main,
    },
    {
      icon: BuildOutlined,
      label: 'Difficulty',
      value: `${difficulty}/10`,
      color: getDifficultyColor(difficulty, theme),
    },
    {
      icon: ErrorOutlineOutlined,
      label: 'Urgency',
      value: urgency,
      color: getUrgencyColor(urgency, theme),
    },
  ];

  return (
    <Card
      variant="outlined"
      sx={{ width: '100%', mt: 1, mb: isFreePlan ? 0 : 2 }}
    >
      <Stack direction="row" alignItems="center" gap={1} mb={{ xs: 2, sm: 3 }}>
        <Text variant="body1" fontWeight="bold">
          Repair Cost Estimate:
        </Text>
        <Text variant="body1">{repair_type}</Text>
      </Stack>
      <Stack
        direction="row"
        alignItems="baseline"
        gap={1}
        mb={{ xs: 0, sm: 1 }}
      >
        <Text variant="h1" fontWeight="bold">
          {commaFormatCurrency(estimated_cost_range.min)} -{' '}
          {commaFormatCurrency(estimated_cost_range.max)}
        </Text>
      </Stack>
      <Stack direction="row" alignItems="center" gap={1} mb={3}>
        <Info sx={{ color: theme.palette.info.main, fontSize: 18 }} />
        <Text variant="body1" color="text.secondary">
          Average Cost in your area is {commaFormatCurrency(average_cost)}
        </Text>
      </Stack>
      <Stack spacing={{ xs: 1, sm: 2 }} mb={3}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Text variant="body1" color="text.secondary">
            Parts Estimated Cost
          </Text>
          <Text variant="body1" fontWeight="bold">
            {commaFormatCurrency(parts_cost_range.min)} -{' '}
            {commaFormatCurrency(parts_cost_range.max)}
          </Text>
        </Stack>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Text variant="body1" color="text.secondary">
            Labor Estimated Cost
          </Text>
          <Text variant="body1" fontWeight="bold">
            {commaFormatCurrency(labor_cost_range.min)} -{' '}
            {commaFormatCurrency(labor_cost_range.max)}
          </Text>
        </Stack>
      </Stack>
      <Divider sx={{ marginY: 2 }} />
      <Stack
        direction="row"
        spacing={{ xs: 1, sm: 2 }}
        mb={2}
        gap={{ xs: 0, sm: 2 }}
        flexWrap="nowrap"
      >
        {cardDetails.map(({ icon: Icon, label, value, color }, index) => (
          <Box
            key={index}
            flex={1}
            minWidth={{ xs: '90px', sm: '120px' }}
            py={2}
            px={{ xs: 1, sm: 2 }}
            bgcolor="background.default"
            borderRadius={2}
            textAlign="center"
          >
            <Icon
              sx={{
                color,
                fontSize: '1.5rem',
                mb: 1,
              }}
            />
            <Text variant="body2" color="text.secondary" mb={0.5}>
              {label}
            </Text>
            <Text variant="body1" fontWeight="bold" sx={{ color }}>
              {value}
            </Text>
          </Box>
        ))}
      </Stack>
      <Divider sx={{ marginY: 2 }} />
      {questions && questions.length > 0 && (
        <Stack spacing={1.5} mb={3}>
          <Stack direction="row" alignItems="center" gap={1}>
            <ForumOutlined sx={{ color: theme.palette.primary.main }} />
            <Text variant="h6" fontWeight="bold" mb={0}>
              Questions to Ask Your Mechanic
            </Text>
          </Stack>
          <Stack spacing={1} display="flex" pl={{ xs: 0, sm: 4 }}>
            {questions.map((question, index) => (
              <Box key={index}>
                <Text variant="body1">{question}</Text>
              </Box>
            ))}
          </Stack>
        </Stack>
      )}
      <Divider sx={{ marginY: 2 }} />
      {pro_tip && (
        <Stack spacing={1.5} borderRadius={2}>
          <Stack direction="row" alignItems="center" gap={1}>
            <PersonPinOutlined sx={{ color: theme.palette.primary.main }} />
            <Text variant="h6" fontWeight="bold" mb={0}>
              Pro tip
            </Text>
          </Stack>
          <Text variant="body1" pl={{ xs: 0, sm: 4 }}>
            {pro_tip}
          </Text>
        </Stack>
      )}
    </Card>
  );
};
